import { gsap } from "gsap";
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useRef, useLayoutEffect } from 'react';

//components
import { NavigationBar } from '../globalComponents/NavigationBar';
import { TableItem } from '../globalComponents/TableItem';
import { ProjectsFooter } from '../globalComponents/ProjectsFooter';
import { useTextAnimation } from '../globalComponents/UseTextAnimation';
import { useImageAnimation } from '../globalComponents/UseImageAnimation';
import { UpButton } from '../globalComponents/UpButton';

//styles
import './AboutMe.css';
import pfp from './pfp.png';
import project_ticketsocket from '../images/project_ticketsocket.webp';
import project_vintach from '../images/project_vintach.webp';
import project_aptia from '../images/project_aptia.webp';
import doldrumdrifters from '../images/doldrumdrifters.webp';

//plugins
gsap.registerPlugin(CSSRulePlugin, ScrollTrigger);

export const AboutMe = ({ className }) => {

    const qualificationsData = [ 
        ['2018-2023', 'Bachelor Degree in Visual Communication Design', 'Universidad de la Republica'],
        ['2022', 'Advanced UX/UI Design Certificate', 'Coderhouse'],
        ['2021', 'Google UX Certificate Program', 'Google'],
        ['2015', 'Graphic Design Certificate', 'BIOS'],
    ];

    const languagesData = [ 
        ['Spanish', 'Native Speaker'],
        ['English', 'Fluent, FCE'],
        ['Italian', 'Improving'],
    ];

    const StuffData = [ 
        ['Unity Design System', 'White label design system for a benefits administration company.', project_aptia, '/Projects/DesignSystem'],
        ['Ticketing Ecosystem', 'Product Ideation & UI Design for a ticketing ecosystem.', project_ticketsocket, '/Projects/Ticketing'],
        ['Vintage Marketplace', 'Branding, Product Ideation & UI Design for a second hand clothing marketplace.', project_vintach, '/Projects/Vintach'],
        ['Other Stuff', 'Personal projects covering illustration, code experiments and more.', doldrumdrifters, '/OtherStuff'],
    ];

    const { textRef, headingRef, bodyRef } = useTextAnimation();
    const { imageRefs } = useImageAnimation(16);

    const imageRef = useRef(null);
    const stickySectionRef = useRef(null);


  useLayoutEffect(() => {

    const ctx = gsap.context(() => {

        ScrollTrigger.matchMedia({

            // Desktop only
            "(min-width: 769px)": () => {

                ScrollTrigger.create({
                    trigger: stickySectionRef.current,

                    start: "top top+=32",

                    end: () => {
                        const contentHeight =
                            document.querySelector(".personal-overview").offsetHeight

                        const imageHeight =
                            imageRef.current.offsetHeight

                        return `+=${contentHeight - imageHeight - 32}`
                    },

                    pin: imageRef.current,
                    pinSpacing: false,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                })

            },

        })

    })

    return () => ctx.revert()

}, [])

    return(
        <div className="subpage-main-container">  
            <div>
                <div>
                    <NavigationBar />

                    <div className="subpage-content">

                        <div className="subpage-heading" ref={headingRef}>
                            <h2 className="heading-2">About Me.</h2>

                            <div className="subpage-subtitle">
                                <p className="subtitle italic">Started designing for fun.</p>
                                <p className="subtitle italic">Now I cry over misaligned pixels.</p>
                            </div>
                        </div>

                        <div 
                            className="subpage-container sticky-section"
                            ref={stickySectionRef}
                        >

                            <div className="personal-overview"> 

                                <p className="body" ref={(el) => (bodyRef.current[0] = el)}>
                                I'm Valentina, a Product Designer and Illustrator based in Uruguay. I care as much about how things look as how they work. My passion lies in creating — inside the digital world and out. I also love music, tea, and old media.
                                </p>

                                <div className="facts-card">

                                    <div className="card-text" ref={(el) => (bodyRef.current[2] = el)}>
                                        <h3 className="heading-5"> Primarily into digital products </h3>

                                        <p className="body">
                                            I spend most of my time building systems, finding ways to improve dev handoff and polishing the details in between.
                                        </p>
                                    </div> 

                                    <div>
                                        <h2 className="heading-3 card-number" ref={(el) => (bodyRef.current[1] = el)}> 1. </h2>
                                    </div> 

                                </div>

                                <div className="facts-card">

                                    <div className="card-text" ref={(el) => (bodyRef.current[3] = el)}>
                                        <h3 className="heading-5"> Often sketching something </h3>

                                        <p className="body">
                                            Prints, illustrated books, anything made with care and a strong visual point of view.
                                        </p>
                                    </div> 

                                    <div>
                                        <h2 className="heading-3 card-number" ref={(el) => (bodyRef.current[4] = el)}> 2. </h2>
                                    </div> 

                                </div>

                                <div className="facts-card">

                                    <div className="card-text" ref={(el) => (bodyRef.current[5] = el)}>
                                        <h3 className="heading-5"> Also into code </h3>

                                        <p className="body">
                                            Design only goes so far on paper, so I've been learning to code to understand the full picture.
                                        </p>
                                    </div> 

                                    <div>
                                        <h2 className="heading-3 card-number" ref={(el) => (bodyRef.current[6] = el)}> 3. </h2>
                                    </div> 

                                </div>

                                <div className="facts-card">

                                    <div className="card-text" ref={(el) => (bodyRef.current[7] = el)}>
                                        <h3 className="heading-5"> Curious about most things </h3>

                                        <p className="body">
                                            Could be editorial, interior design or generative code, it varies by day. I'm always learning something new.
                                        </p>
                                    </div> 

                                    <div>
                                        <h2 className="heading-3 card-number" ref={(el) => (bodyRef.current[8] = el)}> 4. </h2>
                                    </div> 

                                </div>

                            </div>

                            <div className="image-column">
                                <img
                                    className="about-image"
                                    src={pfp}
                                    alt=""
                                    ref={(el) => {
                                        imageRef.current = el;
                                        imageRefs.current[0].current = el;
                                    }}
                                />
                            </div>

                        </div>

                        <div className="subpage-container">

                            <h1 className="heading-3" ref={(el) => (textRef.current[10] = el)}>
                                / Qualifications
                            </h1>

                            <div className="right-section">

                                {qualificationsData.map((data, index) => (
                                    <TableItem 
                                        key={index} 
                                        leftValue={data[0]}
                                        rightTop={data[1]}
                                        rightBottom={data[2]}
                                        className="list-item" 
                                    />
                                ))}

                            </div>

                        </div>

                        <div className="subpage-container">

                            <h1 className="heading-3 aboutme-subheading" ref={(el) => (textRef.current[11] = el)}>
                                / Languages
                            </h1>

                            <div className="right-section">

                                {languagesData.map((data, index) => (
                                    <TableItem 
                                        key={index} 
                                        leftValue={data[0]}
                                        rightTop={data[1]}
                                        className="list-item" 
                                    />
                                ))}

                            </div>

                        </div>

                    </div>

                    <ProjectsFooter StuffData={StuffData} />

                </div>
                <UpButton />

            </div>
        </div>
    )
};